import i18n from 'i18next'
import { phone } from 'phone'
import { match, P } from 'ts-pattern'

import { MetricType } from './charts/types/medical/metric/types'
import { formatDate, secondsToHHMM } from './dateFormatters'

export const formatCurrency = ({ value, label }: { value?: number; label?: string }) => {
    const formattedValue = value ? value.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ' ') : 0
    return `${formattedValue} ${label ?? ''}`
}

export const formatPrice = ({ value, label }: { value?: number; label?: string }) => {
    return value === 0 ? i18n.t('common.ui.freeOfCharge') : formatCurrency({ value, label })
}

export const formatHuf = (value: number): string => formatCurrency({ value, label: 'Ft' })

export const formatPhoneNumber = (phoneNumber: string) => {
    return phoneNumber.replace(/(\d{2})(\d{2})(\d{3})(\d{4})/, '$1 $2 $3 $4')
}

export const formatPhoneNumberWithCountryCode = (phoneNumber: string) => {
    const countryCode = phone(phoneNumber).countryCode
    const number = phone(phoneNumber).phoneNumber

    if (!countryCode) {
        return phoneNumber
    }

    return number?.replace(countryCode, '06')
}

export const formatGender = (gender?: 'MALE' | 'FEMALE' | null) => {
    return match(gender)
        .with('MALE', () => i18n.t('common.user.male'))
        .with('FEMALE', () => i18n.t('common.user.female'))
        .otherwise(() => i18n.t('common.user.notSpecified'))
}

export const phonePlusToZero = (phoneNumber: string) => {
    if (phoneNumber.startsWith('+')) {
        return '00' + phoneNumber.slice(1)
    } else {
        return phoneNumber
    }
}

export const phoneZeroToPlus = (phoneNumber: string) => {
    if (phoneNumber.startsWith('00')) {
        return '+' + phoneNumber.slice(2)
    } else {
        return phoneNumber
    }
}

export const formatIdCard = ({
    type,
    idNumber,
    options,
}: {
    type: 'ssn' | 'passport' | 'idNumber'
    idNumber: string
    options?: { showIdType?: boolean }
}) => {
    const formattedIdNumber = match(type)
        .with('ssn', () => idNumber.replace(/(\d{3})(\d{3})(\d{3})/, '$1-$2-$3'))
        .with(P.union('passport', 'idNumber'), () => idNumber)
        .exhaustive()
    const idType = match(type)
        .with('ssn', () => i18n.t('common.user.socialSecurity'))
        .with('passport', () => i18n.t('common.health.passport'))
        .with('idNumber', () => i18n.t('common.user.idNumber'))
        .exhaustive()
    return options?.showIdType ? `(${idType}) ${formattedIdNumber}` : formattedIdNumber
}

export const formatName = ({
    firstName,
    lastName,
    reverse = true,
}: {
    firstName?: string | null
    lastName?: string | null
    reverse?: boolean
}) => {
    const parts = reverse ? [lastName, firstName] : [firstName, lastName]
    const filteredParts = parts.filter(Boolean)
    return filteredParts.length > 0 ? filteredParts.join(' ') : undefined
}

export const LOCALES = ['hu', 'en'] as const

export type Locale = (typeof LOCALES)[number]

export const DEFAULT_LOCALE: Locale = 'hu'

export const toLocale = (raw?: string | null): Locale => {
    const normalized = raw?.trim().toLowerCase()
    return LOCALES.find((locale) => normalized?.startsWith(locale)) ?? DEFAULT_LOCALE
}

export const formatExpertName = ({
    title,
    firstName,
    lastName,
    locale,
}: {
    title?: string | null
    firstName?: string | null
    lastName?: string | null
    locale?: Locale
}) => {
    const name = formatName({ firstName, lastName, reverse: locale !== 'en' })
    return [title?.trim(), name].filter(Boolean).join(' ')
}

export type ExpertNameSource = {
    title?: string | null
    employee?: { firstName?: string | null; lastName?: string | null } | null
}

export const formatExpertNameFromExpert = (expert?: ExpertNameSource | null, locale?: Locale) =>
    formatExpertName({
        title: expert?.title,
        firstName: expert?.employee?.firstName,
        lastName: expert?.employee?.lastName,
        locale,
    })

export const formatAddress = ({
    country,
    city,
    postalCode,
    street,
}: {
    country?: string | null
    city?: string | null
    postalCode?: string | null
    street?: string | null
}) => {
    const parts = [country, postalCode, city, street].filter(Boolean)
    return parts.length > 0 ? parts.join(', ') : undefined
}

export const formatShippingAddressLine = ({
    country,
    postalCode,
    city,
    street,
}: {
    country?: string | null
    postalCode?: string | null
    city?: string | null
    street?: string | null
}) => [country, `${postalCode ?? ''} ${city ?? ''}`.trim(), street].filter(Boolean).join(', ')

export const formatBirthInfo = ({
    birthPlace,
    birthDate,
}: {
    birthPlace?: string | null
    birthDate?: Date | null
}) => {
    return birthPlace && birthDate ? `${birthPlace}, ${formatDate(birthDate)}` : ''
}

type MetricTypeValue = (typeof MetricType)[keyof typeof MetricType]

const metricPrecision: Record<string, number> = {
    [MetricType.RestingHeartRate]: 0,
    [MetricType.HeartRateVariability]: 0,
    [MetricType.BloodOxygen]: 0,
    [MetricType.DailySteps]: 0,
    [MetricType.BodyHeight]: 0,
    [MetricType.BloodPressure]: 0,
    [MetricType.WaterIntake]: 0,
    [MetricType.BodyWeight]: 1,
    [MetricType.BodyTemperature]: 1,
    [MetricType.BodyMassIndex]: 1,
    [MetricType.BloodSugar]: 1,
    [MetricType.FoetalHeartRate]: 0,
    [MetricType.FoetalHeartRateVariability]: 0,
    [MetricType.UterineActivity]: 0,
    [MetricType.Arrhythmia]: 0,
    [MetricType.AbdominalCircumference]: 1,
}

export const formatMetricValue = (
    metricType: MetricTypeValue,
    value: number | undefined | null,
): string => {
    if (value === undefined || value === null) return '-'

    if (metricType === MetricType.SleepTime) {
        return secondsToHHMM(value)
    }

    if (metricType === MetricType.DailySteps) {
        return Math.round(value).toLocaleString()
    }

    const decimals = metricPrecision[metricType] ?? 0
    return decimals === 0 ? Math.round(value).toString() : value.toFixed(decimals)
}

export const formatBloodPressure = (
    systolic: number | undefined | null,
    diastolic: number | undefined | null,
): string => {
    if (systolic == null || diastolic == null) return '-'
    return `${Math.round(systolic)}/${Math.round(diastolic)}`
}
